// Offer a child to a contracting agency — the one form for it, used by
// Schedule → Agency scheduling and by the child's Schedule tab.
//
// What is asked (role, hours a week, start date, a note) is optional: with
// none of it the child is offered for discussion. The offer lasts 7 days
// unless the office chooses otherwise, the agency is told in its Chat space at
// once, and it answers on its board (lib/agencyOffers.js has the rules).
// Coordinators and above only: the database refuses anyone else.
import { useState } from 'react';
import Modal from '../Modal';
import { supabase } from '../../lib/supabase';
import { ROLE_NEEDED, OFFER_DAY_CHOICES, DEFAULT_OFFER_DAYS, newOfferRow } from '../../lib/agencyOffers';

/**
 * @param {{ id: string, name: string }} p.client   name as the office knows the child
 * @param {{ id: string, name: string }} p.agency
 * @param {object} [p.defaults]  { role_needed, hours_needed } prefilled from the open hours
 */
export default function OfferAgencyModal({ client, agency, defaults = {}, profile, onClose, onDone }) {
  const [form, setForm] = useState({
    role_needed: defaults.role_needed || '', hours_needed: defaults.hours_needed || '',
    start_date: '', note: '', days: DEFAULT_OFFER_DAYS,
  });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  const submit = async () => {
    setBusy(true); setErr('');
    const { error } = await supabase.from('client_agency_offers')
      .insert([newOfferRow({ clientId: client.id, agencyId: agency.id, form, profile })]);
    setBusy(false);
    if (error) {
      setErr(error.code === '23505' ? `${client.name} is already offered to ${agency.name}.` : error.message);
      return;
    }
    onDone?.();
  };

  const label = { fontSize: 11.5, color: 'var(--text3)', display: 'block', marginBottom: 4 };
  return (
    <Modal show onClose={onClose} title={`Offer ${client.name} to ${agency.name}`} error={err}
      footer={<>
        <button className="btn btn-outline" onClick={onClose} disabled={busy}>Cancel</button>
        <button className="btn btn-primary" onClick={submit} disabled={busy}>{busy ? 'Offering…' : 'Offer'}</button>
      </>}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 10 }}>
        <div><label style={label}>Role needed</label>
          <select className="form-select" value={form.role_needed} onChange={set('role_needed')}>
            <option value="">Not specified</option>
            {ROLE_NEEDED.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
          </select></div>
        <div><label style={label}>Hours a week</label>
          <input className="form-input" type="number" min="0" step="0.5" value={form.hours_needed} onChange={set('hours_needed')} placeholder="Optional" /></div>
        <div><label style={label}>Starting</label>
          <input className="form-input" type="date" value={form.start_date} onChange={set('start_date')} /></div>
        <div><label style={label}>Answer within</label>
          <select className="form-select" value={form.days} onChange={set('days')}>
            {OFFER_DAY_CHOICES.map(d => <option key={d} value={d}>{d} days</option>)}
          </select></div>
      </div>
      <div style={{ marginTop: 10 }}>
        <label style={label}>Note to the agency</label>
        <textarea className="form-input" rows={3} maxLength={500} value={form.note} onChange={set('note')}
          placeholder="e.g. weekday nights, 7p–7a; family prefers a Spanish-speaking nurse" />
        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4, lineHeight: 1.5 }}>
          No clinical detail here: it goes into their Chat space. The care plan carries the clinical picture, on their board.
        </div>
      </div>
      <div style={{ fontSize: 11.5, color: 'var(--text2)', marginTop: 12, lineHeight: 1.55, background: 'var(--bg2)', borderRadius: 6, padding: '8px 10px' }}>
        {agency.name} will see the child's first name and last initial, county and these details on their board, with the care plan
        if one is shared. They are told in their Chat space now and answer on the board: <b>We can cover</b> or <b>Can't cover</b>.
        With no answer in {form.days} days the offer expires.
      </div>
    </Modal>
  );
}
